import {
  ChevronLeft,
  ChevronRight,
  Copy,
  ExternalLink,
  LocateFixed,
  Monitor,
  MonitorUp,
  Moon,
  Music,
  Radio,
  RotateCcw,
  User,
  X,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import { useAuth } from "../../../../auth/useAuth";
import ControllersIcon from "../../../../assets/logos/Controlers icon.png";
import HymnsIcon from "../../../../assets/logos/Hymns Icon.png";
import GlobalLoadingPage from "../../../../core/components/GlobalLoadingPage";
import GlobalNotificationPill from "../../../../core/components/GlobalNotificationPill";
import PanelLoadingState from "../../../../core/components/PanelLoadingState";
import useResponsive from "../../../../core/hooks/useResponsive";
import {
  clearChurchLiveScreenIdentify,
  goToChurchLiveSlide,
  identifyChurchLiveScreen,
  loadChurchLiveDisplay,
  putChurchDisplayToSleep,
  quickLiveChurchHymn,
  returnChurchDisplayToLoop,
  startChurchServiceTakeover,
  updateChurchLiveScreenGroup,
  updateChurchLiveScreenName,
} from "../../services/liveDisplayService";
import {
  loadChurchHymns,
  searchChurchHymns,
} from "../../services/hymnService";

const CONTROLLERS = [
  {
    id: "main",
    label: "Main Controller",
    shortLabel: "Main",
    icon: Monitor,
    detail: "Manage connected screens and keep the pre-service loop ready.",
  },
  {
    id: "preacher",
    label: "Preacher Controller",
    shortLabel: "Preach",
    icon: User,
    detail: "Simple slide-to-slide sermon controls.",
  },
  {
    id: "hymns",
    label: "Hymn Service Controller",
    shortLabel: "Hymns",
    icon: Music,
    detail: "Search hymns and send songs live.",
  },
];

const HYMN_CONTROLLER_ANIMATION_CSS = `
  @keyframes hymnControllerFadeIn {
    from { opacity: 0; transform: translateY(8px) scale(0.99); }
    to { opacity: 1; transform: translateY(0) scale(1); }
  }

  @keyframes hymnControllerFloat {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-6px); }
  }

  @keyframes hymnControllerNotePulse {
    0%, 100% {
      box-shadow: 0 0 0 0 rgba(var(--color-primary-rgb),0.22);
      transform: scale(1);
    }
    50% {
      box-shadow: 0 0 0 8px rgba(var(--color-primary-rgb),0);
      transform: scale(1.04);
    }
  }

  @keyframes hymnControllerSongTileIn {
    from {
      opacity: 0;
      transform: translate(-28px, 34px) scale(0.94);
    }
    to {
      opacity: 1;
      transform: translate(0, 0) scale(1);
    }
  }

  @keyframes hymnControllerSoundBar {
    0%, 100% {
      opacity: 0.34;
      transform: scaleY(0.28);
    }
    45% {
      opacity: 0.9;
      transform: scaleY(1);
    }
  }
`;

function getInitialController(preferredController = "") {
  if (CONTROLLERS.some((item) => item.id === preferredController)) {
    return preferredController;
  }

  if (typeof window === "undefined") return "main";
  const controller = new URLSearchParams(window.location.search).get("controller");
  return CONTROLLERS.some((item) => item.id === controller) ? controller : "main";
}

function getSlideLabel(slide, index) {
  if (!slide) return `Slide ${index + 1}`;
  if (slide.songNumber) return `#${slide.songNumber} ${slide.title || "Hymn"}`;
  return slide.title || slide.reference || `Slide ${index + 1}`;
}

function getHymnLabel(hymn = {}) {
  return hymn.title || (hymn.songNumber ? `Song ${hymn.songNumber}` : "Untitled Hymn");
}

function getHymnSearchValue(hymn = {}) {
  return hymn.songNumber ? `#${hymn.songNumber} ${getHymnLabel(hymn)}` : getHymnLabel(hymn);
}

function buildControllerHymnSlides(hymn = {}) {
  return (Array.isArray(hymn.slides) ? hymn.slides : []).map((slide, index) => ({
    id: slide.id || `${hymn.id || "hymn"}-quick-${index + 1}`,
    itemType: "hymn",
    title: slide.title || hymn.title || "Untitled Hymn",
    body: slide.body || slide.text || "",
    text: slide.text || slide.body || "",
    imageUrl: slide.imageUrl || slide.image_url || "",
    imagePath: slide.imagePath || slide.image_path || "",
    storageBucket: slide.storageBucket || slide.storage_bucket || "global-hymn-files",
    songNumber: hymn.songNumber || hymn.song_number || "",
    parentId: hymn.id || "",
    isEndOfSong:
      typeof slide.isEndOfSong === "boolean"
        ? slide.isEndOfSong
        : typeof slide.is_end_of_song === "boolean"
          ? slide.is_end_of_song
          : index === (Array.isArray(hymn.slides) ? hymn.slides.length : 0) - 1,
  }));
}

function getQrUrl(url) {
  return `https://api.qrserver.com/v1/create-qr-code/?size=420x420&margin=16&format=png&data=${encodeURIComponent(url)}`;
}

function getControllerUrl(controllerId) {
  if (typeof window === "undefined") return "";
  if (controllerId === "preacher") return `${window.location.origin}/sermon`;
  return `${window.location.origin}/church/controllers/${controllerId}`;
}

function getMinutesPadUrl() {
  if (typeof window === "undefined") return "";
  return `${window.location.origin}/church/management/minutes-pad`;
}

function formatScreenTimestamp(value) {
  if (!value) return "Just now";

  try {
    return new Date(value).toLocaleString();
  } catch (_error) {
    return value;
  }
}

function withTimeout(promise, fallback) {
  return Promise.race([
    promise,
    new Promise((resolve) => {
      window.setTimeout(() => resolve(fallback), 12000);
    }),
  ]);
}

export default function RemotePage({
  initialController = "",
  showHeader = true,
  standalone = false,
}) {
  const { user } = useAuth();
  const { isPhone } = useResponsive();
  const [activeController, setActiveController] = useState(() =>
    getInitialController(initialController)
  );
  const [display, setDisplay] = useState(null);
  const [screens, setScreens] = useState([]);
  const [hymns, setHymns] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");

  async function refreshControllers() {
    setLoading(true);
    setError("");

    try {
      const [displayResult, hymnResult] = await Promise.all([
        withTimeout(
          loadChurchLiveDisplay(user?.id).catch((loadError) => {
            console.error("Controller display load error:", loadError);
            return { display: null, screens: [] };
          }),
          { display: null, screens: [] }
        ),
        withTimeout(
          loadChurchHymns(user).catch((loadError) => {
            console.error("Controller hymn load error:", loadError);
            return { hymns: [] };
          }),
          { hymns: [] }
        ),
      ]);
      setDisplay(displayResult.display);
      setScreens(displayResult.screens || []);
      setHymns(hymnResult.hymns || []);
    } catch (loadError) {
      console.error("Controllers load error:", loadError);
      setError(loadError?.message || "Could not load church controllers.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refreshControllers();
  }, [user?.id]);

  const slides = display?.slides || [];
  const currentIndex = display?.currentSlideIndex || 0;
  const currentSlide = slides[currentIndex] || null;
  const isLive = display?.state === "live";
  const filteredHymns = useMemo(
    () => searchChurchHymns(hymns, query).slice(0, 18),
    [hymns, query]
  );

  const controllerLinks = useMemo(
    () => CONTROLLERS.map((controller) => ({
      ...controller,
      url: getControllerUrl(controller.id),
    })),
    []
  );
  const minutesPadUrl = useMemo(() => getMinutesPadUrl(), []);

  async function runAction(actionKey, runner, successMessage = "") {
    if (!display || !user?.id) return;

    setBusy(actionKey);
    setError("");
    setNotice("");

    try {
      const result = await runner();
      if (result?.display) setDisplay(result.display);
      if (result?.screens) setScreens(result.screens);
      if (successMessage) setNotice(successMessage);
    } catch (actionError) {
      console.error("Controller action error:", actionError);
      setError(actionError?.message || "Controller action failed.");
    } finally {
      setBusy("");
    }
  }

  function openDisplayOnThisDevice() {
    if (!display?.publicUrl) return;
    window.open(display.publicUrl, "_blank", "noopener,noreferrer");
  }

  async function copyText(value, label) {
    if (!value) return;

    try {
      await window.navigator.clipboard.writeText(value);
      setNotice(`${label} copied.`);
    } catch (_error) {
      setError(`Could not copy ${label.toLowerCase()}.`);
    }
  }

  function startLive() {
    runAction(
      "start-live",
      () => startChurchServiceTakeover(user.id, display),
      "Live display takeover started."
    );
  }

  function returnToLoop(message = "Display returned to the church logo loop.") {
    if (display) {
      setDisplay({
        ...display,
        state: "loop",
        isLive: false,
        currentSlideIndex: 0,
      });
    }

    runAction("return-loop", () => returnChurchDisplayToLoop(user.id, display), message);
  }

  function sleepAllScreens() {
    if (display) {
      setDisplay({
        ...display,
        state: "sleep",
        isLive: false,
        currentSlideIndex: 0,
      });
    }

    runAction("sleep-all", () => putChurchDisplayToSleep(user.id, display), "All screens are sleeping.");
  }

  async function renameScreen(screen, nextName) {
    if (!display || !screen?.screenCode) return;

    const safeName = String(nextName || "").trim() || "Live screen";
    setBusy(`rename-${screen.screenCode}`);
    setError("");
    setNotice("");

    try {
      const result = await updateChurchLiveScreenName(display, screen.screenCode, safeName);
      setScreens((currentScreens) => {
        const sourceScreens = result?.screens?.length ? result.screens : currentScreens;
        return sourceScreens.map((currentScreen) =>
          currentScreen.screenCode === screen.screenCode
            ? { ...currentScreen, name: safeName }
            : currentScreen
        );
      });
      setNotice("Screen name saved.");
    } catch (renameError) {
      console.error("Controller screen rename error:", renameError);
      setError(renameError?.message || "Could not rename screen.");
    } finally {
      setBusy("");
    }
  }

  async function toggleLocateScreen(screen) {
    if (!display || !screen?.screenCode) return;

    const nextStatus = screen.status === "identify" ? "connected" : "identify";
    setBusy(`locate-${screen.screenCode}`);
    setError("");
    setNotice("");

    try {
      let result = null;
      setScreens((currentScreens) =>
        currentScreens.map((currentScreen) =>
          currentScreen.screenCode === screen.screenCode
            ? { ...currentScreen, status: nextStatus }
            : currentScreen
        )
      );

      if (nextStatus === "identify") {
        result = await identifyChurchLiveScreen(display, screen.screenCode);
      } else {
        await clearChurchLiveScreenIdentify(display, screen.screenCode);
        returnChurchDisplayToLoop(user.id, display)
          .then((loopResult) => {
            if (loopResult?.display) setDisplay(loopResult.display);
            if (loopResult?.screens?.length) {
              setScreens(
                loopResult.screens.map((currentScreen) =>
                  currentScreen.screenCode === screen.screenCode
                    ? { ...currentScreen, status: nextStatus }
                    : currentScreen
                )
              );
            }
          })
          .catch((loopError) => {
            console.error("Controller return-to-loop after locate error:", loopError);
          });
      }

      if (result?.display) setDisplay(result.display);
      setScreens((currentScreens) => {
        const sourceScreens = result?.screens?.length ? result.screens : currentScreens;
        return sourceScreens.map((currentScreen) =>
          currentScreen.screenCode === screen.screenCode
            ? { ...currentScreen, status: nextStatus }
            : currentScreen
        );
      });
      setNotice(nextStatus === "identify" ? `Locating ${screen.name || "screen"}.` : "Screen returned to service loop.");
    } catch (locateError) {
      console.error("Controller screen locate error:", locateError);
      setError(locateError?.message || "Could not locate screen.");
    } finally {
      setBusy("");
    }
  }

  async function changeScreenGroup(screen, screenGroup) {
    if (!display || !screen?.screenCode) return;

    setBusy(`group-${screen.screenCode}`);
    setError("");
    setNotice("");

    try {
      const result = await updateChurchLiveScreenGroup(display, screen.screenCode, screenGroup);
      setScreens((currentScreens) => {
        const sourceScreens = result?.screens?.length ? result.screens : currentScreens;
        return sourceScreens.map((currentScreen) =>
          currentScreen.screenCode === screen.screenCode
            ? { ...currentScreen, screenGroup }
            : currentScreen
        );
      });
      setNotice(screenGroup === "interactive" ? "Screen moved to interactive display." : "Screen moved to service display.");
    } catch (groupError) {
      console.error("Controller screen group error:", groupError);
      setError(groupError?.message || "Could not update screen settings.");
    } finally {
      setBusy("");
    }
  }

  function moveToSlide(nextIndex) {
    const maxIndex = Math.max((display?.slides?.length || 1) - 1, 0);
    const safeIndex = Math.min(Math.max(nextIndex, 0), maxIndex);
    const optimisticDisplay = {
      ...display,
      currentSlideIndex: safeIndex,
    };

    setDisplay(optimisticDisplay);
    runAction("slide", () => goToChurchLiveSlide(user.id, optimisticDisplay, safeIndex));
  }

  async function sendHymnLive(hymn) {
    const hymnSlides = buildControllerHymnSlides(hymn);
    const optimisticDisplay = {
      ...display,
      state: "live",
      isReady: true,
      isLive: true,
      selectedServiceItemId: `quick-hymn:${hymn?.id || "unknown"}`,
      currentSlideIndex: 0,
      slides: hymnSlides,
    };

    setDisplay(optimisticDisplay);
    setQuery("");
    await runAction(
      `hymn-${hymn.id}`,
      () => quickLiveChurchHymn(user.id, hymn, optimisticDisplay),
      `${getHymnLabel(hymn)} is live.`
    );
  }

  function nextHymnSlide() {
    if (currentIndex >= slides.length - 1) {
      returnToLoop("Song finished. Display returned to the church logo loop.");
      setQuery("");
      return;
    }

    moveToSlide(currentIndex + 1);
  }

  if (loading) {
    const LoadingComponent = standalone ? GlobalLoadingPage : PanelLoadingState;
    const iconProps = standalone
      ? { iconFallbackOverride: ControllersIcon, iconOverride: ControllersIcon, labelOverride: "Controllers" }
      : { iconAlt: "Controllers", iconSrc: ControllersIcon };

    return (
      <LoadingComponent
        {...iconProps}
        modeOverride="church"
        title="Loading Controllers"
        detail="Preparing display controls, hymn search, and access links..."
      />
    );
  }

  const activeMeta = CONTROLLERS.find((item) => item.id === activeController) || CONTROLLERS[0];
  const isStandaloneHymnController = standalone && activeController === "hymns";

  return (
    <div
      style={{
        ...styles.page,
        ...(showHeader ? styles.pageStandalone : {}),
        ...(isStandaloneHymnController ? styles.hymnControllerStandalone : {}),
      }}
    >
      {showHeader && !isStandaloneHymnController ? (
        <section style={styles.header}>
          <div>
            <div style={styles.eyebrow}>Screen Controllers</div>
            <h2 style={styles.title}>{standalone ? activeMeta.label : "Church Controllers"}</h2>
          </div>
        </section>
      ) : null}

      <GlobalNotificationPill
        message={error || notice}
        onDismiss={() => {
          if (error) {
            setError("");
          } else {
            setNotice("");
          }
        }}
        tone={error ? "error" : "success"}
      />

      {standalone ? (
        <>
          {activeController === "preacher" ? (
            <section style={{ ...styles.statusGrid, ...(isPhone ? styles.statusGridPhone : {}) }}>
              <div style={styles.statusCard}>
                <div style={styles.statusLabel}>Active Controller</div>
                <div style={styles.statusValue}>{activeMeta.label}</div>
                <div style={styles.statusText}>{activeMeta.detail}</div>
              </div>
              <div style={styles.statusCard}>
                <div style={styles.statusLabel}>Display State</div>
                <div style={styles.statePill}>
                  <Radio size={14} />
                  {display?.state || "loop"}
                </div>
                <div style={styles.statusText}>
                  {isLive ? "Taking over the live display." : "Pre-service loop is active."}
                </div>
              </div>
              <button type="button" style={styles.openButton} onClick={openDisplayOnThisDevice}>
                <MonitorUp size={18} />
                Open Display On This Device
              </button>
            </section>
          ) : null}

          {activeController === "hymns" ? (
            <HymnController
              busy={busy}
              currentIndex={currentIndex}
              isPhone={isPhone}
              currentSlide={currentSlide}
              filteredHymns={filteredHymns}
              isLive={isLive}
              liveSlideCount={slides.length}
              onNext={nextHymnSlide}
              onReturnLoop={returnToLoop}
              onSendLive={sendHymnLive}
              query={query}
              setQuery={setQuery}
            />
          ) : activeController === "preacher" ? (
            <PreacherController
              busy={busy}
              isPhone={isPhone}
              currentIndex={currentIndex}
              currentSlide={currentSlide}
              isLive={isLive}
              onNext={() => moveToSlide(Math.min(currentIndex + 1, Math.max(slides.length - 1, 0)))}
              onPrevious={() => moveToSlide(Math.max(currentIndex - 1, 0))}
              onStart={startLive}
              slides={slides}
            />
          ) : (
            <MainController
              busy={busy}
              display={display}
              isPhone={isPhone}
              isLive={isLive}
              onLocateScreen={toggleLocateScreen}
              onRenameScreen={renameScreen}
              onScreenGroupChange={changeScreenGroup}
              onReturnLoop={() => returnToLoop("Display returned to the pre-service loop.")}
              onSleepAll={sleepAllScreens}
              screens={screens}
            />
          )}
        </>
      ) : (
        <section style={{ ...styles.setupPanel, ...(isPhone ? styles.setupPanelPhone : {}) }}>
          <div>
            <div style={styles.statusLabel}>Screen Controllers</div>
            <div style={styles.setupTitle}>Scan or open a dedicated controller device.</div>
            <div style={styles.statusText}>
              Controllers run on their own clean pages with no Church admin navigation.
            </div>
          </div>
          <button type="button" style={styles.openButton} onClick={openDisplayOnThisDevice}>
            <MonitorUp size={18} />
            Open Display On This Device
          </button>
        </section>
      )}

      {!standalone ? (
        <section style={{ ...styles.linkGrid, ...(isPhone ? styles.linkGridPhone : {}) }}>
          <LinkCard
            label="Live Display"
            url={display?.publicUrl}
            isPhone={isPhone}
            onCopy={() => copyText(display?.publicUrl, "Live display link")}
          />
          {controllerLinks.map((link) => (
            <LinkCard
              key={link.id}
              label={link.label}
              url={link.url}
              isPhone={isPhone}
              onCopy={() => copyText(link.url, `${link.label} link`)}
            />
          ))}
          <LinkCard
            label="Meeting Minutes iPad"
            url={minutesPadUrl}
            isPhone={isPhone}
            onCopy={() => copyText(minutesPadUrl, "Meeting Minutes iPad link")}
          />
        </section>
      ) : null}
    </div>
  );
}

function MainController({
  busy,
  display,
  isPhone,
  isLive,
  onLocateScreen,
  onRenameScreen,
  onScreenGroupChange,
  onReturnLoop,
  onSleepAll,
  screens,
}) {
  const [selectedScreenCode, setSelectedScreenCode] = useState("");
  const selectedScreen = (screens || []).find((screen) => (screen.screenCode || screen.id || "") === selectedScreenCode) || null;
  const [screenNameDraft, setScreenNameDraft] = useState("");
  const displayState = display?.state || "loop";
  const serviceScreens = (screens || []).filter((screen) => (screen.screenGroup || "service") !== "interactive").length;
  const interactiveScreens = Math.max((screens || []).length - serviceScreens, 0);

  useEffect(() => {
    if (!selectedScreen) {
      setScreenNameDraft("");
      return;
    }

    setScreenNameDraft(selectedScreen.name || "");
  }, [selectedScreen?.screenCode, selectedScreen?.id, selectedScreen?.name]);

  function closeModal() {
    setSelectedScreenCode("");
  }

  return (
    <section style={styles.mainScreensPanel}>
      <div style={{ ...styles.mainScreensHeader, ...(isPhone ? styles.mainScreensHeaderPhone : {}) }}>
        <div>
          <div style={styles.statusLabel}>Main Controller</div>
          <div style={styles.mainScreensTitle}>Screens</div>
          <div style={styles.statusText}>
            {screens.length === 1 ? "One connected display is ready." : `${screens.length} connected displays are ready.`}
          </div>
        </div>
        <div style={styles.mainScreensStats}>
          <div style={styles.mainScreensStat}>
            <span style={styles.mainScreensStatValue}>{displayState}</span>
            <span style={styles.mainScreensStatLabel}>Display state</span>
          </div>
          <div style={styles.mainScreensStat}>
            <span style={styles.mainScreensStatValue}>{serviceScreens}</span>
            <span style={styles.mainScreensStatLabel}>Service</span>
          </div>
          <div style={styles.mainScreensStat}>
            <span style={styles.mainScreensStatValue}>{interactiveScreens}</span>
            <span style={styles.mainScreensStatLabel}>Interactive</span>
          </div>
        </div>
      </div>

      <div style={{ ...styles.mainScreenGrid, ...(isPhone ? styles.mainScreenGridPhone : {}) }}>
        <button
          type="button"
          style={{ ...styles.mainScreenTile, ...styles.mainActionTile }}
          onClick={onSleepAll}
          disabled={Boolean(busy)}
        >
          <span style={styles.mainActionIconWrap}>
            <Moon size={34} />
          </span>
          <span style={styles.mainScreenTileCopy}>
            <span style={styles.mainScreenTileTitle}>Sleep All Screens</span>
            <span style={styles.mainScreenTileMeta}>Dim every connected display until you wake the loop.</span>
          </span>
        </button>

        <button
          type="button"
          style={{ ...styles.mainScreenTile, ...styles.mainActionTile }}
          onClick={onReturnLoop}
          disabled={Boolean(busy)}
        >
          <span style={styles.mainActionIconWrap}>
            <RotateCcw size={34} />
          </span>
          <span style={styles.mainScreenTileCopy}>
            <span style={styles.mainScreenTileTitle}>Pre-Service Loop</span>
            <span style={styles.mainScreenTileMeta}>Send every display back to the church loop.</span>
          </span>
        </button>

        {screens.length === 0 ? (
          <div style={styles.mainScreensEmpty}>
            <Monitor size={36} />
            <div>
              <div style={styles.mainScreensEmptyTitle}>No screens connected</div>
              <div style={styles.statusText}>Open the live display link on a screen and it will appear here.</div>
            </div>
          </div>
        ) : (
          screens.map((screen) => {
            const screenCode = screen.screenCode || screen.id || "";
            const isLocating = screen.status === "identify";
            const screenGroup = screen.screenGroup || "service";

            return (
              <button
                key={screenCode}
                type="button"
                style={{
                  ...styles.mainScreenTile,
                  ...(isLocating ? styles.mainScreenTileLocating : {}),
                }}
                onClick={() => setSelectedScreenCode(screenCode)}
              >
                <span style={styles.mainScreenIconWrap}>
                  <Monitor size={28} />
                </span>
                <span style={styles.mainScreenTileCopy}>
                  <span style={styles.mainScreenTileTitle}>{screen.name || "Live screen"}</span>
                  <span style={styles.mainScreenTileMeta}>{screenGroup === "interactive" ? "Interactive display" : "Service display"}</span>
                  <span style={styles.mainScreenTileMeta}>{isLocating ? "Locating" : isLive ? "Live display" : "Pre-service loop"}</span>
                  <span style={styles.mainScreenTileMeta}>Last seen {formatScreenTimestamp(screen.lastSeenAt)}</span>
                </span>
              </button>
            );
          })
        )}
      </div>

      {selectedScreen ? (
        <div style={styles.screenModalBackdrop} role="presentation" onClick={closeModal}>
          <div
            style={{ ...styles.screenModal, ...(isPhone ? styles.screenModalPhone : {}) }}
            role="dialog"
            aria-modal="true"
            aria-label={`${selectedScreen.name || "Screen"} controls`}
            onClick={(event) => event.stopPropagation()}
          >
            <div style={styles.screenModalHeader}>
              <div>
                <div style={styles.statusLabel}>Screen Controls</div>
                <div style={styles.screenModalTitle}>{selectedScreen.name || "Live screen"}</div>
              </div>
              <button type="button" style={styles.modalCloseButton} onClick={closeModal} aria-label="Close">
                <X size={18} />
              </button>
            </div>

            <label style={styles.screenModalLabel}>
              Screen Name
              <input
                style={styles.screenModalInput}
                value={screenNameDraft}
                onChange={(event) => setScreenNameDraft(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    onRenameScreen(selectedScreen, screenNameDraft);
                  }
                }}
              />
            </label>

            <div style={styles.screenModalMeta}>ID {selectedScreen.screenCode || selectedScreen.id || "Unknown"}</div>
            <div style={styles.screenModalMeta}>{selectedScreen.deviceInfo || "Display device"}</div>
            <div style={styles.screenModalMeta}>Last seen {formatScreenTimestamp(selectedScreen.lastSeenAt)}</div>

            <div style={styles.screenModalSection}>
              <div style={styles.screenModalSectionTitle}>Screen Type</div>
              <div style={styles.screenGroupToggle}>
                {[
                  ["service", "Service"],
                  ["interactive", "Interactive"],
                ].map(([group, label]) => {
                  const selectedGroup = selectedScreen.screenGroup || "service";
                  const isSelected = selectedGroup === group;

                  return (
                    <button
                      key={group}
                      type="button"
                      style={{
                        ...styles.screenGroupButton,
                        ...(isSelected ? styles.screenGroupButtonActive : {}),
                      }}
                      onClick={() => onScreenGroupChange(selectedScreen, group)}
                      disabled={busy === `group-${selectedScreen.screenCode || selectedScreen.id}`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div style={styles.screenModalActions}>
              <button
                type="button"
                style={styles.primaryButton}
                onClick={() => onRenameScreen(selectedScreen, screenNameDraft)}
                disabled={busy === `rename-${selectedScreen.screenCode || selectedScreen.id}`}
              >
                Save Name
              </button>
              <button
                type="button"
                style={{
                  ...styles.secondaryButton,
                  ...(selectedScreen.status === "identify" ? styles.locateActiveButton : {}),
                }}
                onClick={() => onLocateScreen(selectedScreen)}
                disabled={busy === `locate-${selectedScreen.screenCode || selectedScreen.id}`}
              >
                <LocateFixed size={18} />
                {selectedScreen.status === "identify" ? "Stop Locate" : "Locate"}
              </button>
              <button type="button" style={styles.loopButton} onClick={onReturnLoop} disabled={Boolean(busy)}>
                <RotateCcw size={18} />
                Send Back to Pre-Service Loop
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}

function PreacherController({ busy, currentIndex, currentSlide, isPhone, isLive, onNext, onPrevious, onStart, slides }) {
  return (
    <section style={styles.preacherPanel}>
      <div style={styles.preacherSlide}>
        <div style={styles.preacherCount}>
          Slide {slides.length ? currentIndex + 1 : 0} / {slides.length}
        </div>
        <div style={styles.preacherTitle}>
          {currentSlide ? getSlideLabel(currentSlide, currentIndex) : "No sermon slides ready"}
        </div>
        <div style={styles.preacherBody}>
          {currentSlide?.text || currentSlide?.body || currentSlide?.subtitle || "Start the live takeover when the sermon is ready."}
        </div>
      </div>
      <div style={{ ...styles.preacherControls, ...(isPhone ? styles.preacherControlsPhone : {}) }}>
        <button type="button" style={styles.secondaryButton} onClick={onPrevious} disabled={!isLive || currentIndex <= 0 || Boolean(busy)}>
          <ChevronLeft size={18} />
          Previous
        </button>
        <button type="button" style={styles.primaryButton} onClick={onStart} disabled={Boolean(busy)}>
          {isLive ? "Live Active" : "Take Over"}
        </button>
        <button type="button" style={styles.secondaryButton} onClick={onNext} disabled={!isLive || currentIndex >= slides.length - 1 || Boolean(busy)}>
          Next
          <ChevronRight size={18} />
        </button>
      </div>
    </section>
  );
}

function HymnController({
  busy,
  currentIndex,
  currentSlide,
  filteredHymns,
  isPhone,
  isLive,
  liveSlideCount,
  onNext,
  onReturnLoop,
  onSendLive,
  query,
  setQuery,
}) {
  const [selectedHymn, setSelectedHymn] = useState(null);
  const [previewSlideIndex, setPreviewSlideIndex] = useState(0);
  const searchInputRef = useRef(null);
  const currentHymnParentId = currentSlide?.parentId || currentSlide?.parent_id || "";
  const isHymnLive = isLive && currentSlide?.itemType === "hymn";
  const selectedSlides = Array.isArray(selectedHymn?.slides) ? selectedHymn.slides : [];
  const selectedPreviewSlide = selectedSlides[previewSlideIndex] || selectedSlides[0] || null;
  const selectedSlideCount = selectedSlides.length;
  const isSelectedHymnLive = Boolean(selectedHymn?.id && currentHymnParentId === selectedHymn.id && isHymnLive);
  const activePreviewSlide = isSelectedHymnLive ? currentSlide : selectedPreviewSlide;
  const activeSlideIndex = isSelectedHymnLive ? currentIndex : previewSlideIndex;
  const activeSlideCount = isSelectedHymnLive ? liveSlideCount : selectedSlideCount;
  const isLastSelectedSlide =
    activeSlideCount > 0 &&
    (activeSlideIndex >= activeSlideCount - 1 || Boolean(isSelectedHymnLive && currentSlide?.isEndOfSong));
  const suggestedHymns = query.trim() ? filteredHymns.slice(0, 4) : [];

  useEffect(() => {
    setPreviewSlideIndex(0);
  }, [selectedHymn?.id]);

  useEffect(() => {
    if (!isSelectedHymnLive) return;

    setPreviewSlideIndex(Math.min(currentIndex, Math.max(selectedSlideCount - 1, 0)));
  }, [currentIndex, isSelectedHymnLive, selectedSlideCount]);

  function openHymn(hymn) {
    setSelectedHymn(hymn);
    setQuery(getHymnSearchValue(hymn));
  }

  function goBackToSearch() {
    if (isSelectedHymnLive) {
      onReturnLoop("Display returned to the pre-service loop.");
    }
    setSelectedHymn(null);
    setQuery("");
  }

  function handleNextHymnStep() {
    if (isSelectedHymnLive) {
      const isLiveLastSlide = currentIndex >= Math.max(liveSlideCount - 1, 0);
      onNext();
      if (isLiveLastSlide || currentSlide?.isEndOfSong) {
        setSelectedHymn(null);
        setQuery("");
      }
      return;
    }

    setPreviewSlideIndex((current) => {
      const maxIndex = Math.max(selectedSlideCount - 1, 0);
      return Math.min(current + 1, maxIndex);
    });
  }

  function handleSearchChange(event) {
    setQuery(event.target.value);
  }

  if (selectedHymn) {
    return (
      <section style={styles.hymnFocusPage}>
        {isLastSelectedSlide ? (
          <div style={styles.hymnLastSlideBadge}>Last Slide</div>
        ) : null}
        <div style={styles.hymnFullPreview}>
          {activePreviewSlide?.imageUrl ? (
            <img
              src={activePreviewSlide.imageUrl}
              alt={activePreviewSlide.title || getHymnLabel(selectedHymn)}
              style={styles.hymnFullImage}
            />
          ) : (
            <div style={styles.hymnFullTextSlide}>
              <div style={styles.hymnFullSlideTitle}>
                {activePreviewSlide?.title || getHymnLabel(selectedHymn)}
              </div>
              <div style={styles.hymnFullSlideBody}>
                {activePreviewSlide?.body || activePreviewSlide?.text || "No slide preview available."}
              </div>
            </div>
          )}
        </div>

        <div style={styles.hymnFocusMeta}>
          {selectedHymn.songNumber ? `#${selectedHymn.songNumber} ` : ""}{getHymnLabel(selectedHymn)}
          {" • "}
          Slide {activeSlideCount ? activeSlideIndex + 1 : 0} / {activeSlideCount}
        </div>

        <div style={{ ...styles.hymnBottomControls, ...(isPhone ? styles.hymnBottomControlsPhone : {}) }}>
          <button type="button" style={styles.hymnBottomButton} onClick={goBackToSearch} disabled={Boolean(busy)}>
            <ChevronLeft size={30} strokeWidth={2.5} />
            Back
          </button>
          <div style={{ ...styles.hymnBottomCenterActions, ...(isPhone ? styles.hymnBottomCenterActionsPhone : {}) }}>
            <button type="button" style={styles.hymnBottomSearchButton} onClick={goBackToSearch} disabled={Boolean(busy)}>
              <ChevronLeft size={18} />
              Back to Search
            </button>
            <button
              type="button"
              style={styles.hymnBottomPrimaryButton}
              onClick={() => onSendLive(selectedHymn)}
              disabled={Boolean(busy)}
            >
              <MonitorUp size={18} />
              Send to Live Display
            </button>
          </div>
          <button
            type="button"
            style={styles.hymnBottomButton}
            onClick={handleNextHymnStep}
            disabled={Boolean(busy) || (!isSelectedHymnLive && previewSlideIndex >= Math.max(selectedSlideCount - 1, 0))}
          >
            Next
            <ChevronRight size={30} strokeWidth={2.5} />
          </button>
        </div>
      </section>
    );
  }

  return (
    <section style={styles.hymnSearchPage}>
      <style>{HYMN_CONTROLLER_ANIMATION_CSS}</style>
      <div style={styles.hymnSearchCenter}>
        <div style={styles.hymnIconRow}>
          <img src={HymnsIcon} alt="" style={styles.hymnSearchIcon} />
          <Music size={42} />
          <img src={HymnsIcon} alt="" style={styles.hymnSearchIcon} />
        </div>
        <div style={styles.hymnSearchTitle}>Hymn Controller</div>
        <div style={styles.hymnSearchSubtitle}>Search by number or title, then choose a hymn to preview.</div>

        <div style={styles.hymnSearchBar}>
          <span style={styles.hymnSearchNoteWrap}>
            <Music size={23} />
          </span>
          <span aria-hidden="true" style={styles.hymnSoundBars}>
            {Array.from({ length: 34 }).map((_, index) => (
              <span
                key={index}
                style={{
                  ...styles.hymnSoundBar,
                  animationDelay: `${index * 74}ms`,
                  height: 7 + ((index * 5) % 18),
                }}
              />
            ))}
          </span>
          <input
            ref={searchInputRef}
            value={query}
            onChange={handleSearchChange}
            placeholder="Search hymns"
            style={styles.hymnSearchInput}
            autoFocus
            onKeyDown={(event) => {
              if (event.key === "Enter" && filteredHymns[0]) {
                event.preventDefault();
                openHymn(filteredHymns[0]);
              }
            }}
          />
        </div>

        {suggestedHymns.length > 0 ? (
          <div style={styles.hymnSuggestionList}>
            {suggestedHymns.map((suggestedHymn, index) => (
              <button
                key={`${suggestedHymn.id || getHymnSearchValue(suggestedHymn)}-${query}`}
                type="button"
                style={{
                  ...styles.hymnSuggestionTile,
                  animationDelay: `${index * 54}ms`,
                  ...(isPhone ? styles.hymnSuggestionTilePhone : {}),
                }}
                onClick={() => openHymn(suggestedHymn)}
              >
                <span style={styles.hymnSuggestionIcon}>
                  <Music size={24} />
                </span>
                <span style={styles.hymnSuggestionText}>
                  <span style={styles.hymnSuggestionKicker}>
                    {index === 0 ? "Best Match" : "Match"}
                  </span>
                  <span style={styles.hymnSuggestionTitle}>
                    {suggestedHymn.songNumber ? `#${suggestedHymn.songNumber} ` : ""}
                    {getHymnLabel(suggestedHymn)}
                  </span>
                  <span style={styles.hymnSuggestionMeta}>
                    {(Array.isArray(suggestedHymn.slides) ? suggestedHymn.slides.length : 0) || "No"} slides
                  </span>
                </span>
                <span style={styles.hymnSuggestionChip}>Open</span>
              </button>
            ))}
          </div>
        ) : null}
      </div>
    </section>
  );
}

function SlidePreview({ currentIndex, currentSlide, slides }) {
  return (
    <div style={styles.previewCard}>
      <div style={styles.previewLabel}>Live Preview</div>
      <div style={styles.previewSurface}>
        {currentSlide ? (
          currentSlide.imageUrl ? (
            <img src={currentSlide.imageUrl} alt={getSlideLabel(currentSlide, currentIndex)} style={styles.previewImage} />
          ) : (
            <div>
              <div style={styles.previewTitle}>{getSlideLabel(currentSlide, currentIndex)}</div>
              <div style={styles.previewBody}>{currentSlide.text || currentSlide.body || currentSlide.subtitle}</div>
            </div>
          )
        ) : (
          <div style={styles.previewEmpty}>No live slide selected.</div>
        )}
      </div>
      <div style={styles.previewMeta}>Slide {slides.length ? currentIndex + 1 : 0} / {slides.length}</div>
    </div>
  );
}

function SlideList({ currentIndex, isLive, onSelectSlide, slides }) {
  return (
    <div style={styles.slideList}>
      {slides.map((slide, index) => (
        <button
          key={slide.id || index}
          type="button"
          style={{ ...styles.slideButton, ...(index === currentIndex ? styles.slideButtonActive : {}) }}
          onClick={() => onSelectSlide(index)}
          disabled={!isLive}
        >
          <span style={styles.slideNumber}>{index + 1}</span>
          <span style={styles.slideText}>{getSlideLabel(slide, index)}</span>
        </button>
      ))}
    </div>
  );
}

function LinkCard({ isPhone, label, onCopy, url }) {
  if (!url) return null;

  return (
    <div style={styles.linkCard}>
      <div style={{ ...styles.linkHeader, ...(isPhone ? styles.linkHeaderPhone : {}) }}>
        <div>
          <div style={styles.linkLabel}>{label}</div>
          <div style={styles.linkUrl}>{url}</div>
        </div>
        <div style={{ ...styles.linkActions, ...(isPhone ? styles.linkActionsPhone : {}) }}>
          <button type="button" style={styles.iconButton} onClick={onCopy} title="Copy link">
            <Copy size={16} />
          </button>
          <a href={url} target="_blank" rel="noreferrer" style={styles.iconButton} title="Open link">
            <ExternalLink size={16} />
          </a>
        </div>
      </div>
      <img src={getQrUrl(url)} alt={`${label} QR code`} style={styles.qrImage} />
    </div>
  );
}

const styles = {
  page: {
    display: "grid",
    gap: 14,
  },
  pageStandalone: {
    background: "#f4f8fb",
    boxSizing: "border-box",
    minHeight: "100vh",
    padding: 14,
  },
  hymnControllerStandalone: {
    height: "100dvh",
    minHeight: "100dvh",
    overflow: "hidden",
    padding: 0,
  },
  header: {
    alignItems: "center",
    display: "flex",
    justifyContent: "space-between",
  },
  eyebrow: {
    color: "var(--color-primary-dark)",
    fontSize: 12,
    fontWeight: 900,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
  },
  title: {
    fontSize: 24,
    margin: "4px 0 0",
  },
  controllerTabs: {
    display: "grid",
    gap: 8,
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
  },
  tabButton: {
    alignItems: "center",
    background: "rgba(255,255,255,0.72)",
    border: "1px solid rgba(15,23,42,0.08)",
    borderRadius: 16,
    color: "#334155",
    cursor: "pointer",
    display: "inline-flex",
    font: "inherit",
    fontSize: 13,
    fontWeight: 850,
    gap: 8,
    justifyContent: "center",
    minHeight: 44,
    padding: "8px 10px",
  },
  tabButtonActive: {
    background: "var(--color-primary)",
    color: "#fff",
  },
  mainScreensPanel: {
    display: "grid",
    gap: 18,
    margin: "0 auto",
    maxWidth: 1040,
    width: "100%",
  },
  mainScreensHeader: {
    alignItems: "end",
    display: "grid",
    gap: 14,
    gridTemplateColumns: "minmax(0, 1fr) auto",
    padding: "4px 2px 2px",
  },
  mainScreensHeaderPhone: {
    gridTemplateColumns: "1fr",
  },
  mainScreensTitle: {
    color: "#223327",
    fontSize: 34,
    fontWeight: 950,
    lineHeight: 1.04,
    marginTop: 4,
  },
  mainScreensStats: {
    display: "grid",
    gap: 8,
    gridTemplateColumns: "repeat(3, minmax(92px, 1fr))",
  },
  mainScreensStat: {
    background: "rgba(255,255,255,0.72)",
    border: "1px solid rgba(215, 224, 211, 0.92)",
    borderRadius: 8,
    boxShadow: "0 12px 28px rgba(37, 53, 45, 0.08)",
    display: "grid",
    gap: 3,
    minHeight: 58,
    padding: "10px 12px",
  },
  mainScreensStatValue: {
    color: "#223327",
    fontSize: 18,
    fontWeight: 950,
    lineHeight: 1.1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    textTransform: "capitalize",
    whiteSpace: "nowrap",
  },
  mainScreensStatLabel: {
    color: "#66766c",
    fontSize: 11,
    fontWeight: 850,
    letterSpacing: 0,
  },
  mainScreensEmpty: {
    alignItems: "center",
    background: "rgba(255,255,255,0.82)",
    border: "1px dashed rgba(95, 125, 77, 0.34)",
    borderRadius: 8,
    color: "#53665a",
    display: "flex",
    gap: 14,
    gridColumn: "1 / -1",
    padding: 18,
  },
  mainScreensEmptyTitle: {
    color: "#223327",
    fontSize: 18,
    fontWeight: 950,
  },
  mainScreenGrid: {
    display: "grid",
    gap: 14,
    gridTemplateColumns: "repeat(auto-fill, minmax(270px, 1fr))",
  },
  mainScreenGridPhone: {
    gridTemplateColumns: "1fr",
  },
  mainScreenTile: {
    appearance: "none",
    WebkitAppearance: "none",
    background: "#ffffff",
    border: "1px solid transparent",
    borderRadius: 18,
    boxShadow: "0 12px 26px rgba(23,32,51,0.08)",
    color: "#172033",
    cursor: "pointer",
    display: "grid",
    gap: 16,
    gridTemplateRows: "auto minmax(78px, 1fr)",
    minHeight: 218,
    outline: "none",
    padding: 20,
    textAlign: "left",
    transition: "box-shadow 160ms ease, transform 160ms ease",
    width: "100%",
  },
  mainActionTile: {
    alignContent: "center",
    alignItems: "center",
    background: "linear-gradient(135deg, rgba(255,255,255,0.94), rgba(248,250,252,0.82))",
    justifyItems: "center",
    textAlign: "center",
  },
  mainScreenTileLocating: {
    background: "#fff7ed",
    borderColor: "#fed7aa",
  },
  mainActionIconWrap: {
    alignItems: "center",
    background: "rgba(var(--color-primary-rgb),0.10)",
    border: "1px solid transparent",
    borderRadius: 999,
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.65)",
    color: "var(--color-primary-dark)",
    display: "inline-flex",
    height: 92,
    justifyContent: "center",
    width: 92,
  },
  mainScreenIconWrap: {
    alignItems: "center",
    background: "linear-gradient(145deg, rgba(255,255,255,0.94), rgba(229,237,224,0.84))",
    border: "1px solid rgba(255,255,255,0.92)",
    borderRadius: 999,
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.82), 0 12px 24px rgba(95, 125, 77, 0.14)",
    color: "var(--color-primary-dark)",
    display: "flex",
    height: 74,
    justifyContent: "center",
    width: 74,
  },
  mainScreenTileCopy: {
    display: "grid",
    gap: 8,
    minWidth: 0,
  },
  mainScreenTileTitle: {
    color: "#223327",
    fontSize: 21,
    fontWeight: 950,
    lineHeight: 1.16,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "normal",
  },
  mainScreenTileMeta: {
    color: "#667085",
    fontSize: 13,
    fontWeight: 800,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  screenModalBackdrop: {
    alignItems: "center",
    background: "rgba(15,23,42,0.42)",
    bottom: 0,
    display: "flex",
    justifyContent: "center",
    left: 0,
    padding: 18,
    position: "fixed",
    right: 0,
    top: 0,
    zIndex: 120,
  },
  screenModal: {
    background: "#ffffff",
    border: "1px solid #d8e5ef",
    borderRadius: 20,
    boxShadow: "0 28px 80px rgba(15,23,42,0.24)",
    display: "grid",
    gap: 14,
    maxWidth: 520,
    padding: 18,
    width: "100%",
  },
  screenModalPhone: {
    maxWidth: "none",
  },
  screenModalHeader: {
    alignItems: "start",
    display: "flex",
    gap: 12,
    justifyContent: "space-between",
  },
  screenModalTitle: {
    color: "#0f172a",
    fontSize: 24,
    fontWeight: 950,
    lineHeight: 1.1,
    marginTop: 4,
  },
  modalCloseButton: {
    alignItems: "center",
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 999,
    color: "#0f172a",
    cursor: "pointer",
    display: "inline-flex",
    height: 38,
    justifyContent: "center",
    width: 38,
  },
  screenModalLabel: {
    color: "#64748b",
    display: "grid",
    fontSize: 11,
    fontWeight: 950,
    gap: 7,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
  },
  screenModalInput: {
    background: "#f8fafc",
    border: "1px solid #cbd5e1",
    borderRadius: 12,
    color: "#0f172a",
    font: "inherit",
    fontSize: 18,
    fontWeight: 900,
    minHeight: 48,
    outline: "none",
    padding: "8px 12px",
    textTransform: "none",
  },
  screenModalMeta: {
    color: "#64748b",
    fontSize: 12,
    fontWeight: 800,
  },
  screenModalSection: {
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 14,
    display: "grid",
    gap: 10,
    padding: 12,
  },
  screenModalSectionTitle: {
    color: "#64748b",
    fontSize: 11,
    fontWeight: 950,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
  },
  screenGroupToggle: {
    display: "grid",
    gap: 8,
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  },
  screenGroupButton: {
    background: "#ffffff",
    border: "1px solid #d8e5ef",
    borderRadius: 12,
    color: "#334155",
    cursor: "pointer",
    font: "inherit",
    fontSize: 13,
    fontWeight: 900,
    minHeight: 42,
    padding: "8px 10px",
  },
  screenGroupButtonActive: {
    background: "var(--color-primary)",
    borderColor: "var(--color-primary)",
    color: "#ffffff",
  },
  screenModalActions: {
    display: "grid",
    gap: 10,
  },
  locateActiveButton: {
    background: "#fff7ed",
    borderColor: "#fed7aa",
    color: "#9a3412",
  },
  statusGrid: {
    display: "grid",
    gap: 12,
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
  },
  statusGridPhone: {
    gridTemplateColumns: "1fr",
  },
  statusCard: {
    background: "rgba(255,255,255,0.72)",
    border: "1px solid rgba(255,255,255,0.56)",
    borderRadius: 18,
    boxShadow: "0 12px 30px rgba(15,23,42,0.08)",
    padding: 14,
  },
  statusLabel: {
    color: "#64748b",
    fontSize: 11,
    fontWeight: 900,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
  },
  statusValue: {
    color: "#0f172a",
    fontSize: 17,
    fontWeight: 900,
    marginTop: 5,
  },
  statusText: {
    color: "#64748b",
    fontSize: 13,
    lineHeight: 1.45,
    marginTop: 5,
  },
  statePill: {
    alignItems: "center",
    color: "#0f172a",
    display: "inline-flex",
    fontSize: 18,
    fontWeight: 900,
    gap: 8,
    marginTop: 4,
    textTransform: "capitalize",
  },
  openButton: {
    alignItems: "center",
    background: "#0f172a",
    border: "none",
    borderRadius: 18,
    color: "#fff",
    cursor: "pointer",
    display: "inline-flex",
    font: "inherit",
    fontSize: 14,
    fontWeight: 900,
    gap: 8,
    justifyContent: "center",
    padding: 14,
  },
  setupPanel: {
    alignItems: "center",
    background: "#fff",
    border: "1px solid #d8e5ef",
    borderRadius: 18,
    boxShadow: "0 8px 20px rgba(15,23,42,0.06)",
    display: "flex",
    gap: 16,
    justifyContent: "space-between",
    padding: 16,
  },
  setupPanelPhone: {
    alignItems: "stretch",
    flexDirection: "column",
  },
  setupTitle: {
    color: "#0f172a",
    fontSize: 20,
    fontWeight: 900,
    marginTop: 5,
  },
  controlLayout: {
    display: "grid",
    gap: 12,
    gridTemplateColumns: "minmax(260px, 1.1fr) minmax(220px, 0.7fr) minmax(240px, 0.8fr)",
  },
  controlLayoutPhone: {
    gridTemplateColumns: "1fr",
  },
  hymnLayout: {
    display: "grid",
    gap: 14,
    gridTemplateColumns: "minmax(300px, 0.92fr) minmax(320px, 1.08fr)",
  },
  hymnLayoutPhone: {
    gridTemplateColumns: "1fr",
  },
  hymnSearchPage: {
    alignItems: "center",
    animation: "hymnControllerFadeIn 420ms ease-out both",
    display: "flex",
    height: "100dvh",
    justifyContent: "center",
    overflow: "hidden",
    padding: "clamp(16px, 4vh, 42px) 12px",
    position: "relative",
  },
  hymnSearchCenter: {
    alignItems: "center",
    display: "flex",
    flexDirection: "column",
    gap: 14,
    maxWidth: 780,
    width: "100%",
  },
  hymnIconRow: {
    alignItems: "center",
    color: "var(--color-primary-dark)",
    display: "flex",
    gap: 18,
    justifyContent: "center",
    marginBottom: 2,
  },
  hymnSearchIcon: {
    animation: "hymnControllerFloat 3.8s ease-in-out infinite",
    display: "block",
    height: 58,
    objectFit: "contain",
    width: 58,
  },
  hymnSearchTitle: {
    color: "#0f172a",
    fontSize: "clamp(28px, 5vw, 58px)",
    fontWeight: 950,
    lineHeight: 1,
    textAlign: "center",
  },
  hymnSearchSubtitle: {
    color: "#64748b",
    fontSize: "clamp(14px, 1.5vw, 18px)",
    fontWeight: 800,
    lineHeight: 1.4,
    textAlign: "center",
  },
  hymnSearchBar: {
    alignItems: "center",
    background: "#ffffff",
    border: "1px solid #d8e5ef",
    borderRadius: 22,
    boxShadow:
      "0 18px 50px rgba(15,23,42,0.1), 0 0 0 0 rgba(var(--color-primary-rgb),0.28)",
    color: "var(--color-primary-dark)",
    display: "flex",
    gap: 12,
    marginTop: 8,
    maxWidth: 700,
    padding: "0 18px",
    position: "relative",
    transition: "border-color 180ms ease, box-shadow 180ms ease, transform 180ms ease",
    width: "100%",
  },
  hymnSoundBars: {
    alignItems: "end",
    bottom: 8,
    display: "flex",
    gap: 5,
    height: 24,
    justifyContent: "space-between",
    left: 18,
    opacity: 0.72,
    pointerEvents: "none",
    position: "absolute",
    right: 18,
    zIndex: 0,
  },
  hymnSoundBar: {
    animation: "hymnControllerSoundBar 980ms ease-in-out infinite",
    background: "linear-gradient(180deg, rgba(var(--color-primary-rgb),0.62), rgba(var(--color-primary-rgb),0.16))",
    borderRadius: 999,
    display: "block",
    flex: "1 1 0",
    maxWidth: 6,
    minWidth: 3,
    transformOrigin: "bottom",
  },
  hymnSearchNoteWrap: {
    alignItems: "center",
    animation: "hymnControllerNotePulse 1.8s ease-in-out infinite",
    background: "rgba(var(--color-primary-rgb),0.12)",
    border: "1px solid rgba(var(--color-primary-rgb),0.16)",
    borderRadius: 999,
    display: "inline-flex",
    flex: "0 0 auto",
    height: 42,
    justifyContent: "center",
    position: "relative",
    width: 42,
    zIndex: 1,
  },
  hymnSearchInput: {
    background: "transparent",
    border: "none",
    color: "#0f172a",
    flex: 1,
    font: "inherit",
    fontSize: "clamp(20px, 2.5vw, 30px)",
    fontWeight: 850,
    minHeight: 72,
    minWidth: 0,
    outline: "none",
    position: "relative",
    zIndex: 1,
  },
  hymnSuggestionList: {
    display: "grid",
    gap: 10,
    marginTop: 4,
    maxWidth: 700,
    width: "100%",
  },
  hymnSuggestionTile: {
    alignItems: "center",
    animation: "hymnControllerSongTileIn 320ms cubic-bezier(.2,.9,.25,1.08) both",
    background: "rgba(255,255,255,0.94)",
    border: "1px solid #d8e5ef",
    borderRadius: 18,
    boxShadow: "0 18px 48px rgba(15,23,42,0.16)",
    color: "#0f172a",
    cursor: "pointer",
    display: "grid",
    gap: 12,
    gridTemplateColumns: "48px minmax(0, 1fr) auto",
    maxWidth: 430,
    minHeight: 78,
    padding: "12px 14px",
    position: "relative",
    alignSelf: "flex-start",
    textAlign: "left",
    transition: "transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease",
    width: "min(430px, 100%)",
    zIndex: 5,
  },
  hymnSuggestionTilePhone: {
    gridTemplateColumns: "42px minmax(0, 1fr)",
    padding: 12,
    width: "100%",
  },
  hymnSuggestionIcon: {
    alignItems: "center",
    background: "rgba(var(--color-primary-rgb),0.12)",
    border: "1px solid rgba(var(--color-primary-rgb),0.18)",
    borderRadius: 14,
    color: "var(--color-primary-dark)",
    display: "inline-flex",
    height: 48,
    justifyContent: "center",
    width: 48,
  },
  hymnSuggestionText: {
    display: "grid",
    gap: 3,
    minWidth: 0,
  },
  hymnSuggestionKicker: {
    color: "#64748b",
    fontSize: 10,
    fontWeight: 950,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
  },
  hymnSuggestionTitle: {
    color: "#0f172a",
    fontSize: 17,
    fontWeight: 950,
    lineHeight: 1.12,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  hymnSuggestionMeta: {
    color: "#64748b",
    fontSize: 12,
    fontWeight: 850,
  },
  hymnSuggestionChip: {
    background: "#0f172a",
    borderRadius: 999,
    color: "#fff",
    fontSize: 11,
    fontWeight: 950,
    padding: "8px 11px",
    textTransform: "uppercase",
  },
  hymnSearchResults: {
    display: "grid",
    gap: 8,
    maxHeight: "min(44vh, 360px)",
    maxWidth: 700,
    overflow: "auto",
    width: "100%",
  },
  hymnSearchResult: {
    alignItems: "center",
    background: "rgba(255,255,255,0.86)",
    border: "1px solid #d8e5ef",
    borderRadius: 16,
    boxShadow: "0 8px 18px rgba(15,23,42,0.05)",
    color: "#0f172a",
    cursor: "pointer",
    display: "grid",
    gap: 12,
    gridTemplateColumns: "minmax(74px, auto) minmax(0, 1fr) auto",
    minHeight: 58,
    padding: "10px 14px",
    textAlign: "left",
    width: "100%",
  },
  hymnSearchResultNumber: {
    color: "#9f1239",
    fontSize: 18,
    fontWeight: 950,
    whiteSpace: "nowrap",
  },
  hymnSearchResultTitle: {
    color: "#0f172a",
    fontSize: 16,
    fontWeight: 900,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  hymnSearchResultMeta: {
    color: "#64748b",
    fontSize: 12,
    fontWeight: 850,
    whiteSpace: "nowrap",
  },
  hymnFocusPage: {
    boxSizing: "border-box",
    display: "grid",
    gap: 10,
    gridTemplateRows: "minmax(0, 1fr) auto auto",
    height: "100dvh",
    overflow: "hidden",
    padding: 10,
    position: "relative",
  },
  hymnLastSlideBadge: {
    background: "rgba(15, 23, 42, 0.92)",
    border: "1px solid rgba(255,255,255,0.22)",
    borderRadius: 999,
    boxShadow: "0 16px 42px rgba(15,23,42,0.22)",
    color: "#ffffff",
    fontSize: "clamp(12px, 1.4vw, 18px)",
    fontWeight: 950,
    left: "50%",
    letterSpacing: "0.08em",
    padding: "10px 16px",
    position: "absolute",
    textTransform: "uppercase",
    top: 18,
    transform: "translateX(-50%)",
    zIndex: 10,
  },
  hymnFocusHeader: {
    alignItems: "center",
    display: "flex",
    gap: 12,
    justifyContent: "space-between",
  },
  hymnFocusTitle: {
    color: "#0f172a",
    fontSize: "clamp(22px, 3.4vw, 42px)",
    fontWeight: 950,
    lineHeight: 1.08,
    marginTop: 4,
  },
  hymnFullPreview: {
    alignItems: "center",
    background: "#ffffff",
    border: "1px solid #d8e5ef",
    borderRadius: 24,
    boxShadow: "0 18px 56px rgba(15,23,42,0.1)",
    display: "flex",
    justifyContent: "center",
    minHeight: 0,
    overflow: "hidden",
    padding: 10,
  },
  hymnFullImage: {
    display: "block",
    height: "100%",
    maxHeight: "100%",
    maxWidth: "100%",
    objectFit: "contain",
    width: "100%",
  },
  hymnFullTextSlide: {
    color: "#0f172a",
    maxWidth: 900,
    textAlign: "center",
  },
  hymnFullSlideTitle: {
    fontSize: "clamp(32px, 6vw, 88px)",
    fontWeight: 950,
    lineHeight: 1.05,
  },
  hymnFullSlideBody: {
    color: "#334155",
    fontSize: "clamp(20px, 3vw, 42px)",
    lineHeight: 1.35,
    marginTop: 22,
    whiteSpace: "pre-line",
  },
  hymnFocusMeta: {
    color: "#64748b",
    fontSize: 13,
    fontWeight: 900,
    textAlign: "center",
  },
  hymnBottomControls: {
    background: "rgba(255,255,255,0.88)",
    border: "1px solid #d8e5ef",
    borderRadius: 20,
    boxShadow: "0 16px 44px rgba(15,23,42,0.12)",
    display: "grid",
    gap: 10,
    gridTemplateColumns: "minmax(280px, 1fr) auto minmax(280px, 1fr)",
    padding: 8,
  },
  hymnBottomControlsPhone: {
    gridTemplateColumns: "1fr",
  },
  hymnBottomCenterActions: {
    display: "grid",
    gap: 8,
    gridTemplateColumns: "auto auto",
    justifyContent: "center",
    minWidth: 0,
  },
  hymnBottomCenterActionsPhone: {
    gridTemplateColumns: "1fr",
  },
  hymnBottomButton: {
    alignItems: "center",
    background: "#0f172a",
    border: "none",
    borderRadius: 22,
    color: "#ffffff",
    cursor: "pointer",
    display: "inline-flex",
    font: "inherit",
    fontSize: "clamp(18px, 1.8vw, 24px)",
    fontWeight: 950,
    gap: 12,
    justifyContent: "center",
    lineHeight: 1,
    minHeight: 46,
    width: "100%",
    padding: "10px 44px",
  },
  hymnBottomPrimaryButton: {
    alignItems: "center",
    background: "#385cff",
    border: "1px solid rgba(56, 92, 255, 0.82)",
    borderRadius: 999,
    boxShadow: "0 14px 34px rgba(56, 92, 255, 0.24)",
    color: "#ffffff",
    cursor: "pointer",
    display: "inline-flex",
    font: "inherit",
    fontSize: 13,
    fontWeight: 950,
    gap: 6,
    justifyContent: "center",
    lineHeight: 1.1,
    minHeight: 34,
    minWidth: 0,
    overflow: "hidden",
    padding: "6px 10px",
    textAlign: "center",
  },
  hymnBottomSearchButton: {
    alignItems: "center",
    background: "rgba(255,255,255,0.94)",
    border: "1px solid #d8e5ef",
    borderRadius: 999,
    color: "#0f172a",
    cursor: "pointer",
    display: "inline-flex",
    font: "inherit",
    fontSize: 15,
    fontWeight: 950,
    gap: 8,
    justifyContent: "center",
    minHeight: 52,
    padding: "12px 14px",
    whiteSpace: "nowrap",
  },
  hymnStagePanel: {
    background: "#fff",
    border: "1px solid #d8e5ef",
    borderRadius: 18,
    boxShadow: "0 8px 20px rgba(15,23,42,0.06)",
    display: "grid",
    gap: 12,
    padding: 14,
  },
  hymnStageHeader: {
    alignItems: "start",
    display: "flex",
    gap: 12,
    justifyContent: "space-between",
  },
  hymnStageTitle: {
    color: "#0f172a",
    fontSize: 22,
    fontWeight: 950,
    lineHeight: 1.08,
    marginTop: 4,
  },
  hymnLiveBadge: {
    alignItems: "center",
    background: "#ecfdf5",
    border: "1px solid #bbf7d0",
    borderRadius: 999,
    color: "#047857",
    display: "inline-flex",
    flexShrink: 0,
    fontSize: 12,
    fontWeight: 950,
    gap: 6,
    padding: "7px 10px",
    textTransform: "uppercase",
  },
  hymnTransportGrid: {
    display: "grid",
    gap: 10,
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
  },
  hymnTransportGridPhone: {
    gridTemplateColumns: "1fr",
  },
  hymnQueuePanel: {
    background: "#fff",
    border: "1px solid #d8e5ef",
    borderRadius: 18,
    boxShadow: "0 8px 20px rgba(15,23,42,0.06)",
    display: "grid",
    gap: 12,
    padding: 14,
  },
  hymnQueueHeader: {
    alignItems: "center",
    display: "flex",
    gap: 12,
    justifyContent: "space-between",
  },
  hymnQueueTitle: {
    color: "#0f172a",
    fontSize: 18,
    fontWeight: 950,
    marginTop: 3,
  },
  hymnCountPill: {
    alignItems: "center",
    background: "#edf4fa",
    border: "1px solid #d8e5ef",
    borderRadius: 999,
    color: "var(--color-primary-dark)",
    display: "inline-flex",
    fontSize: 13,
    fontWeight: 950,
    justifyContent: "center",
    minWidth: 38,
    padding: "7px 10px",
  },
  preacherPanel: {
    background: "#fff",
    border: "1px solid #d8e5ef",
    borderRadius: 22,
    display: "grid",
    gap: 18,
    padding: 18,
  },
  preacherSlide: {
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 20,
    minHeight: 260,
    padding: 22,
  },
  preacherCount: {
    color: "#64748b",
    fontSize: 13,
    fontWeight: 900,
    textTransform: "uppercase",
  },
  preacherTitle: {
    color: "#0f172a",
    fontSize: "clamp(24px, 4vw, 54px)",
    fontWeight: 900,
    lineHeight: 1.08,
    marginTop: 18,
  },
  preacherBody: {
    color: "#334155",
    fontSize: "clamp(16px, 2vw, 26px)",
    lineHeight: 1.45,
    marginTop: 18,
    whiteSpace: "pre-line",
  },
  preacherControls: {
    display: "grid",
    gap: 10,
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
  },
  preacherControlsPhone: {
    gridTemplateColumns: "1fr",
  },
  previewCard: {
    background: "#fff",
    border: "1px solid #d8e5ef",
    borderRadius: 18,
    boxShadow: "0 8px 20px rgba(15,23,42,0.06)",
    display: "grid",
    gap: 10,
    padding: 14,
  },
  previewLabel: {
    color: "#64748b",
    fontSize: 11,
    fontWeight: 900,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
  },
  previewSurface: {
    alignItems: "center",
    aspectRatio: "16 / 9",
    background: "#fff",
    border: "1px solid #d8e5ef",
    borderRadius: 16,
    display: "flex",
    justifyContent: "center",
    overflow: "hidden",
    padding: 14,
    textAlign: "center",
  },
  previewImage: {
    maxHeight: "100%",
    maxWidth: "100%",
    objectFit: "contain",
    width: "100%",
  },
  previewTitle: {
    color: "#0f172a",
    fontSize: "clamp(18px, 2vw, 30px)",
    fontWeight: 900,
    lineHeight: 1.1,
  },
  previewBody: {
    color: "#334155",
    fontSize: 14,
    lineHeight: 1.45,
    marginTop: 10,
    whiteSpace: "pre-line",
  },
  previewEmpty: {
    color: "#64748b",
    fontSize: 14,
  },
  previewMeta: {
    color: "#64748b",
    fontSize: 13,
    fontWeight: 800,
  },
  commandPanel: {
    background: "#fff",
    border: "1px solid #d8e5ef",
    borderRadius: 18,
    display: "grid",
    gap: 10,
    padding: 14,
  },
  buttonGrid: {
    display: "grid",
    gap: 10,
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  },
  buttonGridPhone: {
    gridTemplateColumns: "1fr",
  },
  primaryButton: {
    alignItems: "center",
    background: "#9f1239",
    border: "none",
    borderRadius: 14,
    color: "#fff",
    cursor: "pointer",
    display: "inline-flex",
    font: "inherit",
    fontSize: 14,
    fontWeight: 900,
    gap: 8,
    justifyContent: "center",
    minHeight: 48,
    padding: "12px 14px",
  },
  secondaryButton: {
    alignItems: "center",
    background: "var(--color-primary)",
    border: "none",
    borderRadius: 14,
    color: "#fff",
    cursor: "pointer",
    display: "inline-flex",
    font: "inherit",
    fontSize: 14,
    fontWeight: 900,
    gap: 8,
    justifyContent: "center",
    minHeight: 48,
    padding: "12px 14px",
  },
  loopButton: {
    background: "#0f172a",
    border: "none",
    borderRadius: 14,
    color: "#fff",
    cursor: "pointer",
    font: "inherit",
    fontSize: 14,
    fontWeight: 900,
    minHeight: 48,
    padding: "12px 14px",
  },
  slideList: {
    background: "#fff",
    border: "1px solid #d8e5ef",
    borderRadius: 18,
    display: "grid",
    gap: 8,
    maxHeight: 420,
    overflow: "auto",
    padding: 12,
  },
  slideButton: {
    alignItems: "center",
    background: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: 12,
    cursor: "pointer",
    display: "flex",
    gap: 10,
    padding: 10,
    textAlign: "left",
  },
  slideButtonActive: {
    background: "#edf4fa",
    borderColor: "#7ea0c2",
  },
  slideNumber: {
    color: "var(--color-primary-dark)",
    fontSize: 12,
    fontWeight: 900,
  },
  slideText: {
    color: "#0f172a",
    fontSize: 13,
    fontWeight: 800,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  searchPanel: {
    background: "#fff",
    border: "1px solid #d8e5ef",
    borderRadius: 18,
    display: "grid",
    gap: 12,
    padding: 14,
  },
  hymnPlanNotice: {
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 12,
    color: "#475569",
    fontSize: 12,
    fontWeight: 800,
    lineHeight: 1.45,
    padding: "10px 12px",
  },
  searchWrap: {
    alignItems: "center",
    background: "#f8fafc",
    border: "1px solid #d8e5ef",
    borderRadius: 14,
    display: "flex",
    gap: 8,
    padding: "0 12px",
  },
  searchInput: {
    background: "transparent",
    border: "none",
    flex: 1,
    font: "inherit",
    fontSize: 15,
    minHeight: 44,
    outline: "none",
  },
  hymnResults: {
    display: "grid",
    gap: 8,
    maxHeight: 620,
    overflow: "auto",
  },
  hymnButton: {
    alignItems: "center",
    background: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: 14,
    cursor: "pointer",
    display: "grid",
    gap: 9,
    gridTemplateColumns: "34px minmax(58px, auto) minmax(0, 1fr) auto",
    minWidth: 0,
    minHeight: 62,
    padding: "10px 12px",
    textAlign: "left",
    width: "100%",
  },
  hymnButtonSelected: {
    background: "#edf4fa",
    borderColor: "#7ea0c2",
    boxShadow: "0 0 0 1px rgba(126, 160, 135, 0.2)",
  },
  hymnButtonLive: {
    background: "#f0fdf4",
    borderColor: "#86efac",
  },
  hymnOrder: {
    alignItems: "center",
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 10,
    color: "#64748b",
    display: "inline-flex",
    fontSize: 12,
    fontWeight: 950,
    height: 34,
    justifyContent: "center",
    width: 34,
  },
  hymnNumber: {
    color: "#9f1239",
    fontSize: 17,
    fontWeight: 950,
    whiteSpace: "nowrap",
  },
  hymnTitleBlock: {
    display: "grid",
    gap: 3,
    minWidth: 0,
  },
  hymnTitle: {
    color: "#0f172a",
    fontSize: 14,
    fontWeight: 900,
    lineHeight: 1.18,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  hymnMeta: {
    color: "#64748b",
    fontSize: 11,
    fontWeight: 800,
  },
  hymnCueChip: {
    background: "#0f172a",
    borderRadius: 999,
    color: "#fff",
    fontSize: 11,
    fontWeight: 950,
    padding: "7px 10px",
    textTransform: "uppercase",
  },
  hymnLiveChip: {
    background: "#047857",
    borderRadius: 999,
    color: "#fff",
    fontSize: 11,
    fontWeight: 950,
    padding: "7px 10px",
    textTransform: "uppercase",
  },
  hymnEmptyState: {
    background: "#f8fafc",
    border: "1px dashed #cbd5e1",
    borderRadius: 14,
    color: "#64748b",
    fontSize: 13,
    fontWeight: 800,
    padding: 16,
    textAlign: "center",
  },
  hymnControlPanel: {
    display: "grid",
    gap: 10,
  },
  selectedHymnPanel: {
    background: "#fff",
    border: "1px solid #d8e5ef",
    borderRadius: 18,
    display: "grid",
    gap: 12,
    padding: 14,
  },
  selectedHymnMeta: {
    color: "#0f172a",
    display: "grid",
    gap: 4,
    minWidth: 0,
  },
  selectedHymnLabel: {
    color: "#64748b",
    fontSize: 11,
    fontWeight: 900,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
  },
  linkGrid: {
    display: "grid",
    gap: 12,
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  },
  linkGridPhone: {
    gridTemplateColumns: "1fr",
  },
  linkCard: {
    background: "#fff",
    border: "1px solid #d8e5ef",
    borderRadius: 18,
    display: "grid",
    gap: 12,
    padding: 14,
  },
  linkHeader: {
    alignItems: "start",
    display: "flex",
    gap: 10,
    justifyContent: "space-between",
  },
  linkHeaderPhone: {
    flexDirection: "column",
  },
  linkLabel: {
    color: "#0f172a",
    fontSize: 14,
    fontWeight: 900,
  },
  linkUrl: {
    color: "#64748b",
    fontSize: 11,
    lineHeight: 1.35,
    marginTop: 4,
    overflowWrap: "anywhere",
  },
  linkActions: {
    display: "flex",
    gap: 6,
  },
  linkActionsPhone: {
    width: "100%",
  },
  iconButton: {
    alignItems: "center",
    background: "#f8fafc",
    border: "1px solid #d8e5ef",
    borderRadius: 10,
    color: "#0f172a",
    cursor: "pointer",
    display: "inline-flex",
    height: 34,
    justifyContent: "center",
    width: 34,
  },
  qrImage: {
    border: "1px solid #e2e8f0",
    borderRadius: 14,
    justifySelf: "center",
    maxWidth: 156,
    width: "100%",
  },
  error: {
    background: "#fef2f2",
    border: "1px solid #fecaca",
    borderRadius: 12,
    color: "#b91c1c",
    fontSize: 13,
    fontWeight: 700,
    padding: "12px 14px",
  },
  notice: {
    background: "#ecfdf5",
    border: "1px solid #bbf7d0",
    borderRadius: 12,
    color: "#047857",
    fontSize: 13,
    fontWeight: 800,
    padding: "12px 14px",
  },
};
